import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Button, Container, Nav, Navbar, Stack } from "react-bootstrap";
import {
  faRightFromBracket,
  faBars,
  faClose,
  faHouse,
  faCaretDown,
  faCaretUp,
  faClipboardUser,
  faBuildingColumns,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { fetchOrganizations, logout, patchUser, switchOrg } from "../../services/httpClient";
import MainHeaderMenu from "./MainHeaderMenu";
import useApp from "../../utils/useApp";
import { unsavedChanges } from "../../App";
import SaveChangesModal from "./SaveChangesModal";
import SelectObject from "./SelectObject";
import { Tooltip } from "react-tooltip";

const MainHeader = () => {
  const { auth, setAuth, showMenu, setShowMenu, activePage, setActivePage, orgs, setOrgs }: any = useApp();
  const [activeMyCases, setActiveMyCases] = useState<boolean>(false);
  const [activeUsers, setActiveUsers] = useState<boolean>(false);
  const [activeCases, setActiveCases] = useState<boolean>(false);
  const [activeGroups, setActiveGroups] = useState<boolean>(false);
  const [activeAnnouncements, setActiveAnnouncements] = useState<boolean>(false);
  const [activeShelter, setActiveShelter] = useState<boolean>(false);
  const [activeReports, setActiveReports] = useState<boolean>(false);
  const [org, setOrg] = useState<string>(auth?.profile?.userOrgID);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [pendingRoute, setPendingRoute] = useState<string>("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [pendingOrg, setPendingOrg] = useState<string>("");
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const currentVersion = localStorage.getItem("appVersion");
  const homepage = auth?.profile?.homepage ? auth?.profile?.homepage : "/";
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const userToggleRef = useRef<HTMLButtonElement | null>(null);
  const userMenuPortalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!userMenuOpen) return;

    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        userMenuPortalRef.current &&
        !userMenuPortalRef.current.contains(target) &&
        userToggleRef.current &&
        !userToggleRef.current.contains(target)
      ) {
        setUserMenuOpen(false);
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };

    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("keydown", handleEsc);

    if (userMenuPortalRef.current) {
      userMenuPortalRef.current.style.setProperty("z-index", "5", "important");
    }

    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [userMenuOpen]);

  let navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    window.addEventListener("resize", handleResize);

    setShowMenu(false);
    getOrgs();
    navigate(activePage);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getOrgs = () => {
    var myOrgs = auth?.profile?.allOrgIDs;

    fetchOrganizations(myOrgs)
      .then((res: any) => {
        var _orgs = res[0].data;
        var orgObjects = _orgs.map((o: any) => {
          return { key: o?.id, name: o?.name };
        });
        setOrgs(orgObjects);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleHome = () => {
    setIsSwitching(false);

    if (unsavedChanges.value !== true) {
      setActivePage(homepage);
      navigate(homepage);
    } else {
      setShowModal(true);
      setPendingRoute("/");
    }
  };

  const handleOpenCloseMenu = () => {
    setShowMenu(!showMenu);
  };

  const routeChange = (route: string) => {
    if (unsavedChanges.value !== true) {
      var tempRoute = route;
      if (route.startsWith("/users")) {
        tempRoute = "/users";
      }

      setActivePage(tempRoute.split("/"[1]));

      switch (tempRoute) {
        case "/settings":
          setActivePage("/settings");
          break;
        case "/my-cases":
          setActivePage("/my-cases");
          break;
        case "/users":
          setActivePage("/users");
          break;
        case "/cases":
          setActivePage("/cases");
          break;
        case "/groups":
          setActivePage("/groups");
          break;
        case "/announcements":
          setActivePage("/announcements");
          break;
        case "/shelter":
          setActivePage("/shelter");
          break;
        case "/reports":
          setActivePage("/reports");
          break;
        default:
          setActivePage("");
          break;
      }
      navigate(route);
    }
  };

  const handleLogout = () => {
    setIsSwitching(false);

    if (unsavedChanges.value !== true) {
      logout()
        .then((res: any) => {
          navigate("/login");
        })
        .catch((err: any) => {
          console.log(err);
        });
    } else {
      setShowModal(true);
      setPendingRoute("/login");
    }
  };

  const handleProfile = () => {
    setIsSwitching(false);

    if (unsavedChanges.value !== true) {
      setActivePage("/");
      navigate("/users/user/" + auth?.profile.id + "?profile=true");
    } else {
      setShowModal(true);
      setPendingRoute("/users/user/" + auth?.profile.id + "?profile=true");
    }
  };

  //menu selection
  useEffect(() => {
    const basePageUrl = activePage.split("/")[1];
    switch (basePageUrl) {
      case "my-cases":
        setActiveMyCases(true);
        setActiveUsers(false);
        setActiveCases(false);
        setActiveGroups(false);
        setActiveAnnouncements(false);
        setActiveShelter(false);
        setActiveReports(false);
        break;
      case "users":
        setActiveMyCases(false);
        setActiveUsers(true);
        setActiveCases(false);
        setActiveGroups(false);
        setActiveAnnouncements(false);
        setActiveShelter(false);
        setActiveReports(false);
        break;
      case "cases":
        setActiveMyCases(false);
        setActiveUsers(false);
        setActiveCases(true);
        setActiveGroups(false);
        setActiveAnnouncements(false);
        setActiveShelter(false);
        setActiveReports(false);
        break;
      case "groups":
        setActiveMyCases(false);
        setActiveUsers(false);
        setActiveCases(false);
        setActiveGroups(true);
        setActiveAnnouncements(false);
        setActiveShelter(false);
        setActiveReports(false);
        break;
      case "announcements":
        setActiveMyCases(false);
        setActiveUsers(false);
        setActiveCases(false);
        setActiveGroups(false);
        setActiveAnnouncements(true);
        setActiveShelter(false);
        setActiveReports(false);
        break;
      case "shelter":
        setActiveMyCases(false);
        setActiveUsers(false);
        setActiveCases(false);
        setActiveGroups(false);
        setActiveAnnouncements(false);
        setActiveShelter(true);
        setActiveReports(false);
        break;
      case "reports":
        setActiveMyCases(false);
        setActiveUsers(false);
        setActiveCases(false);
        setActiveGroups(false);
        setActiveAnnouncements(false);
        setActiveShelter(false);
        setActiveReports(true);
        break;
      default:
        setActiveMyCases(false);
        setActiveUsers(false);
        setActiveCases(false);
        setActiveGroups(false);
        setActiveAnnouncements(false);
        setActiveShelter(false);
        setActiveReports(false);
        break;
    }
  }, [activePage]);

  const handleSwitchOrg = (orgId: string) => {
    if (unsavedChanges.value !== true) {
      setOrg(orgId);
      localStorage.removeItem("auth");
      localStorage.removeItem("expiration");
      setShowMenu(false);

      switchOrg(orgId)
        .then(async (res: any) => {
          const profile = res[0].data;
          const includeHMIS = profile.includeHMIS;
          let homepage = profile.homepage || "/";

          if (!includeHMIS && homepage === "/shelter") {
            homepage = "/";

            try {
              await patchUser({ homepage }, profile.id);
            } catch (err) {
              console.log(err);
            }
          }

          setAuth({ profile: { ...profile, homepage }, twofactor: true });
          unsavedChanges.value = false;
          setActivePage(homepage);
          navigate(homepage);
        })
        .catch((err: any) => {
          console.log(err);
        });
    } else {
      setShowModal(true);
      setPendingRoute("/my-cases");
      setPendingOrg(orgId);
      setIsSwitching(true);
    }
  };

  return (
    <>
      <Navbar variant="dark" className="nav nav-header">
        <Container className="custom-container px-5 mx-auto">
          <div className="ps-5">
            <Stack direction="horizontal">
              <Navbar.Brand
                data-tooltip-id="tooltip_main"
                data-tooltip-content={currentVersion}
                className="cursor-pointer"
                onClick={() => handleHome()}
              >
                {isMobile ? (
                  <img src="/AndGo_Logo_White_Only.png" height="35" alt="And Go" />
                ) : (
                  <img src="/AndGo_Logo_White_Horizontal.png" width="220" height="35" alt="And Go" />
                )}
              </Navbar.Brand>
              <Tooltip id="tooltip_main" place="left" style={{ padding: "5px 10px", zIndex: "1200" }} />
              <SelectObject
                label="Organization"
                value={org}
                options={orgs}
                skipInitialOption={true}
                cssClasses={
                  orgs.length === 1 ? "app-primary-alt-color no-caret org-header-width" : "app-primary-alt-color org-header-width"
                }
                onUpdate={(e: any) => handleSwitchOrg(e.target.value)}
                disabled={orgs.length > 1 ? false : true}
              />
            </Stack>
          </div>
          <div className="pe-5">
            <Stack direction="horizontal">
              {auth?.profile?.role !== "support" ? (
                <Button className={"bg-transparent border-0 button-menu me-3"} onClick={() => handleOpenCloseMenu()}>
                  {showMenu ? <FontAwesomeIcon icon={faClose} className="me-2" /> : <FontAwesomeIcon icon={faBars} className="me-2" />}
                  Menu
                </Button>
              ) : (
                <Button className={"bg-transparent border-0 button-menu"} onClick={() => handleHome()}>
                  <FontAwesomeIcon icon={faHouse} className="me-2" />
                  My Cases
                </Button>
              )}
              <div>
                <button
                  ref={userToggleRef}
                  className="btn"
                  style={{ color: "white", background: "transparent", border: "none", padding: 0 }}
                  onClick={() => setUserMenuOpen((s) => !s)}
                  aria-expanded={userMenuOpen}
                >
                  Welcome {auth?.profile?.preferredname ? auth?.profile?.preferredname : auth?.profile?.firstname}
                  <FontAwesomeIcon icon={userMenuOpen ? faCaretUp : faCaretDown} className="ms-1" />
                </button>

                {userMenuOpen &&
                  userToggleRef.current &&
                  createPortal(
                    <div
                      ref={userMenuPortalRef}
                      className="header-dropdown-menu-portal dropdown-menu show"
                      style={{
                        position: "absolute",
                        top: userToggleRef.current.getBoundingClientRect().bottom + window.scrollY,
                        left: userToggleRef.current.getBoundingClientRect().left + window.scrollX,
                        border: "1px solid rgba(0,0,0,.15)",
                        borderRadius: "0.375rem",
                      }}
                      role="menu"
                    >
                      <button
                        className="dropdown-item app-header-text"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setShowMenu(false);
                          handleProfile();
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        My Profile
                      </button>
                      {auth?.profile?.role === "org_admin" && (
                        <button
                          className="dropdown-item app-header-text"
                          onClick={() => {
                            setUserMenuOpen(false);
                            setShowMenu(false);
                            routeChange("/settings");
                          }}
                        >
                          <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
                          Organization
                        </button>
                      )}
                      {auth?.profile?.role !== "support" && (
                        <button
                          className="dropdown-item app-header-text"
                          onClick={() => {
                            setUserMenuOpen(false);
                            setShowMenu(false);
                            routeChange("/checkin");
                          }}
                        >
                          <FontAwesomeIcon icon={faClipboardUser} className="me-2" />
                          Check In / Out
                        </button>
                      )}
                      <button
                        className="dropdown-item app-header-text"
                        onClick={() => {
                          handleLogout();
                        }}
                      >
                        <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
                        Sign Out
                      </button>
                    </div>,
                    document.body,
                  )}
              </div>
              {/* <Nav.Link className="app-light-text" onClick={() => handleLogout()}>
                <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
                {!isMobile && "Sign Out"}
              </Nav.Link> */}
            </Stack>
          </div>
        </Container>
      </Navbar>
      <Navbar className="nav-base"></Navbar>
      {showMenu && (
        <MainHeaderMenu
          activeMyCases={activeMyCases}
          activeUsers={activeUsers}
          activeCases={activeCases}
          activeGroups={activeGroups}
          activeAnnouncements={activeAnnouncements}
          activeReports={activeReports}
          activeShelter={activeShelter}
          routeChange={routeChange}
        />
      )}
      {isSwitching ? (
        <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={() => handleSwitchOrg(pendingOrg)} />
      ) : (
        <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path={pendingRoute} />
      )}
    </>
  );
};

export default MainHeader;
